/**
 * Companion control-plane client scaffold.
 * CO1 replaces stub with real fetch to companion.camlinconnect.com.au control plane.
 */

import { parseCompanionSiteConfig } from './config';
import { createStubCompanionSiteConfig } from './stubConfigProvider';
import type {
  CompanionBootResult,
  CompanionControlPlaneClient,
  CompanionControlPlaneFetchOptions
} from './types';

export const DEFAULT_COMPANION_CONTROL_PLANE_BASE =
  'https://companion.camlinconnect.com.au';

export interface CompanionControlPlaneClientOptions {
  /** When true (default in dev), use local stub instead of network fetch. */
  useStub?: boolean;
  fetchImpl?: typeof fetch;
}

export function createCompanionControlPlaneClient(
  options: CompanionControlPlaneClientOptions = {}
): CompanionControlPlaneClient {
  // TODO(CO1): default to false once companion.camlinconnect.com.au control plane ships.
  const useStub = options.useStub ?? true;
  const fetchImpl = options.fetchImpl || fetch;

  return {
    async fetchSiteConfig(
      fetchOptions: CompanionControlPlaneFetchOptions
    ): Promise<CompanionBootResult> {
      if (useStub) {
        const config = createStubCompanionSiteConfig(fetchOptions.siteKey);
        return { ok: true, config };
      }

      const baseUrl = fetchOptions.controlPlaneBaseUrl || DEFAULT_COMPANION_CONTROL_PLANE_BASE;
      const url = `${baseUrl}/api/companion/config/${encodeURIComponent(fetchOptions.siteKey)}`;

      try {
        const response = await fetchImpl(url, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Origin: fetchOptions.origin
          },
          credentials: 'omit'
        });

        if (response.status === 403) {
          return {
            ok: false,
            refusalReason: 'origin_refused',
            message: 'Origin not allowed for this site key'
          };
        }

        if (!response.ok) {
          return {
            ok: false,
            refusalReason: 'config_unavailable',
            message: `Control plane responded ${response.status}`
          };
        }

        const payload = await response.json();
        const parsed = parseCompanionSiteConfig(payload, fetchOptions.siteKey);
        if (!parsed.ok || !parsed.config) {
          return {
            ok: false,
            refusalReason: 'config_unavailable',
            message: parsed.errors.join('; ')
          };
        }

        return { ok: true, config: parsed.config };
      } catch (error) {
        return {
          ok: false,
          refusalReason: 'config_unavailable',
          message: error instanceof Error ? error.message : 'Network error'
        };
      }
    }
  };
}
